import { useState } from 'react'

import { Button, Input } from '@bs/ui-components'

import { DialogField, EditDialog } from '@/components/ui/edit-dialog'
import { ApiError } from '@/services/http'
import { checkPhoneVerification, startPhoneVerification } from '@/services/account'

/**
 * Legacy signup's Twilio Verify step: send a code by SMS, type it back. Two calls, the same copy
 * legacy answered with ("Phone number is verified" / "Phone number is not verified").
 */
export function PhoneVerifyDialog({ phone, onClose, onVerified }: { phone: string; onClose(): void; onVerified(): void }) {
  const [stage, setStage] = useState<'send' | 'code'>('send')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function sendCode() {
    setBusy(true)
    setError('')
    try {
      const result = await startPhoneVerification(phone)
      setMessage(`Code sent to ${result.to}.`)
      setStage('code')
    } catch (cause) {
      setError(cause instanceof ApiError && cause.status === 501 ? 'Phone verification is not available yet.' : 'Could not send the code — please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function checkCode() {
    if (!code.trim()) {
      setError('Enter the code from the SMS.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const result = await checkPhoneVerification(phone, code.trim())
      setMessage(result.message)
      if (result.status === 'verified') onVerified()
      else setError(result.message)
    } catch {
      setError('Could not check the code — please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <EditDialog
      title="Verify phone number"
      submitLabel={stage === 'send' ? 'Send code' : 'Verify'}
      onClose={onClose}
      onSubmit={() => void (stage === 'send' ? sendCode() : checkCode())}
      submitDisabled={busy}
    >
      <p className="text-sm text-muted-foreground">We will text a verification code to <strong>{phone}</strong>.</p>
      {stage === 'code' ? (
        <DialogField label="Verification code">
          <Input value={code} onChange={(event) => setCode(event.target.value)} placeholder="6-digit code" inputMode="numeric" autoFocus />
        </DialogField>
      ) : null}
      {message ? <p className="text-sm text-muted-foreground">{message}</p> : null}
      {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
      {stage === 'code' ? <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void sendCode()}>Resend code</Button> : null}
    </EditDialog>
  )
}
